import { useTranslation } from "react-i18next";
import type { PhaseSpec } from "./PhaseStrip";

/**
 * Canon section ScreenPlanSolo 16-week macro scrubber (main.dc.html:846-855):
 * dotted timeline за period c current week marker + race flag. До v153
 * у нас был только PhaseStrip (phase boxes c labels) — он остаётся как
 * drill-down detail; scrubber добавляет thin season-level orientation
 * («сколько недель прошло, сколько осталось»).
 *
 * Pure SVG render — no hooks beyond translation. Phase boundaries
 * derived из phases[].end_date — vertical separators между phase chunks.
 */
export interface PlanWeekScrubberProps {
  /** Все phases плана — total weeks derived from last phase's end_week_index + 1. */
  phases: PhaseSpec[];
  /** Current week index (1-based), computed by parent (planStart → today). */
  currentWeek: number;
  /** Optional race day ISO; renders flag marker on its week column. */
  raceDateISO?: string | null;
  className?: string;
}

const W = 320;
const H = 28;
const PAD_X = 6;
const DOT_R = 2.5;
const CURRENT_R = 5;

export function PlanWeekScrubber({
  phases,
  currentWeek,
  raceDateISO,
  className = "",
}: PlanWeekScrubberProps) {
  const { t } = useTranslation("plan");

  if (phases.length === 0) return null;

  const planStartISO = phases[0].start;
  const planEndISO = phases[phases.length - 1].end;

  const [psY, psM, psD] = planStartISO.split("-").map(Number);
  const [peY, peM, peD] = planEndISO.split("-").map(Number);
  const planStart = new Date(psY, psM - 1, psD);
  const planEnd = new Date(peY, peM - 1, peD);
  const totalDays = Math.max(
    7,
    Math.round((planEnd.getTime() - planStart.getTime()) / 86400_000) + 1,
  );
  const totalWeeks = Math.max(1, Math.round(totalDays / 7));

  const innerW = W - PAD_X * 2;
  const xFor = (weekIdx: number) =>
    PAD_X + ((weekIdx - 0.5) / totalWeeks) * innerW;

  // Phase boundary lines at fractional positions.
  const boundaries: number[] = [];
  if (phases.length > 1) {
    const startMs = planStart.getTime();
    const endMs = planEnd.getTime();
    const span = endMs - startMs || 1;
    for (let i = 0; i < phases.length - 1; i++) {
      const [ey, em, ed] = phases[i].end.split("-").map(Number);
      const bMs = new Date(ey, em - 1, ed).getTime();
      const fr = Math.max(0, Math.min(1, (bMs - startMs) / span));
      boundaries.push(PAD_X + fr * innerW);
    }
  }

  // Race flag position.
  let raceX: number | null = null;
  if (raceDateISO) {
    const [ry, rm, rd] = raceDateISO.split("-").map(Number);
    const rMs = new Date(ry, rm - 1, rd).getTime();
    const fr = Math.max(
      0,
      Math.min(1, (rMs - planStart.getTime()) / Math.max(1, planEnd.getTime() - planStart.getTime())),
    );
    raceX = PAD_X + fr * innerW;
  }

  const dots: number[] = [];
  for (let i = 1; i <= totalWeeks; i++) dots.push(i);

  const currentXSafe = xFor(Math.max(1, Math.min(totalWeeks, currentWeek)));
  const baseY = H / 2;

  return (
    <div
      data-testid="plan-week-scrubber"
      className={`indigo-card px-3 py-2 ${className}`}
      aria-label={t("scrubber.aria", {
        defaultValue: "Шкала недель сезона",
      })}
    >
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
          {t("scrubber.eyebrow", {
            defaultValue: "СЕЗОН · {{n}} / {{total}}",
            n: Math.max(1, Math.min(totalWeeks, currentWeek)),
            total: totalWeeks,
          })}
        </div>
        {raceDateISO && (
          <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-fg-muted num-display">
            {t("scrubber.race_flag", { defaultValue: "▲ ГОНКА" })}
          </div>
        )}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        style={{ display: "block" }}
      >
        {/* Baseline rail. */}
        <line
          x1={PAD_X}
          x2={W - PAD_X}
          y1={baseY}
          y2={baseY}
          stroke="var(--bg-divider)"
          strokeWidth={1}
        />
        {/* Phase boundary separators. */}
        {boundaries.map((x, i) => (
          <line
            key={i}
            x1={x}
            x2={x}
            y1={baseY - 6}
            y2={baseY + 6}
            stroke="var(--fg-muted)"
            strokeWidth={1}
            opacity={0.6}
            data-testid={`plan-scrubber-boundary-${i}`}
          />
        ))}
        {/* Week dots. */}
        {dots.map((wk) => (
          <circle
            key={wk}
            cx={xFor(wk)}
            cy={baseY}
            r={DOT_R}
            fill={wk < currentWeek ? "var(--fg-muted)" : "var(--fg-secondary)"}
            opacity={wk < currentWeek ? 0.5 : 0.9}
          />
        ))}
        {/* Current week ring. */}
        <circle
          cx={currentXSafe}
          cy={baseY}
          r={CURRENT_R}
          fill="var(--accent)"
          stroke="var(--bg-card)"
          strokeWidth={1.5}
          data-testid="plan-scrubber-current"
        />
        {/* Race flag marker — triangle pointing down at race week. */}
        {raceX != null && (
          <g data-testid="plan-scrubber-race-flag">
            <polygon
              points={`${raceX},${baseY - 9} ${raceX - 4},${baseY - 14} ${raceX + 4},${baseY - 14}`}
              fill="var(--ok)"
            />
            <line
              x1={raceX}
              x2={raceX}
              y1={baseY - 6}
              y2={baseY + 6}
              stroke="var(--ok)"
              strokeWidth={1.5}
            />
          </g>
        )}
      </svg>
    </div>
  );
}
